import React, { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Space,
  Typography,
  Modal,
  Form,
  Input,
  message,
} from "antd";
import {
  ClockCircleOutlined,
  PlusOutlined,
  KeyOutlined,
  ExclamationCircleOutlined,
  CrownOutlined,
  UserOutlined,
  MailOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import { CreateOrganizationModal } from "./CreateOrganizationModal";
import { useAuth0 } from "@auth0/auth0-react";
import { useOrganization } from "../contexts/organization-context";
import { useSubscription } from "../contexts/subscription-context";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../utils/axios";

const { Text } = Typography;

interface AccessStatus {
  has_access: boolean;
  reason?: string;
  organizations: Array<{
    id: string;
    name: string;
    subscription_status?: string;
    free_trial_expires_at?: string;
    is_owner: boolean;
  }>;
  needs_organization: boolean;
  user_trial_expires_at?: string;
  trial_days_remaining?: number;
}

interface TrialBannerProps {
  onTrialUpdate?: () => void;
}

export const TrialBanner: React.FC<TrialBannerProps> = ({ onTrialUpdate }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { refreshOrganizations, isOrganizationOwner } = useOrganization();
  const { hasActiveSubscription, subscriptionData, isInOrg } = useSubscription();
  const navigate = useNavigate();
  const [accessStatus, setAccessStatus] = useState<AccessStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchAccessStatus = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await apiClient.get("/api/access/status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccessStatus(response.data);
      console.log("[TrialBanner] Access status:", response.data);
      console.log("[TrialBanner] Subscription data:", subscriptionData);
    } catch (error) {
      console.error("Error fetching access status:", error);
      setAccessStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccessStatus();
  }, []);

  // Refresh access status when subscription data changes
  useEffect(() => {
    if (subscriptionData?.is_free_trial || hasActiveSubscription) {
      console.log("[TrialBanner] Subscription context updated, refreshing access status");
      fetchAccessStatus();
    }
  }, [subscriptionData?.is_free_trial, hasActiveSubscription]);

  const shouldShowBanner = () => {
    if (loading || !accessStatus) return false;
    
    // Don't show banner if user already has an active subscription
    if (hasActiveSubscription) {
      console.log("[TrialBanner] Not showing banner - user has active subscription or org trial");
      return false;
    }
    
    // Show banner for users with user-level trials or who need organizations
    const shouldShow = (accessStatus.trial_days_remaining !== undefined && accessStatus.trial_days_remaining >= 0) ||
           accessStatus.needs_organization;
    
    console.log("[TrialBanner] Should show banner:", shouldShow, {
      trialDaysRemaining: accessStatus.trial_days_remaining,
      needsOrganization: accessStatus.needs_organization,
      hasActiveSubscription,
      isFreeTrial: subscriptionData?.is_free_trial
    });
    
    return shouldShow;
  };

  const getTrialDaysRemaining = () => {
    return accessStatus?.trial_days_remaining || 0;
  };

  const getBannerType = () => {
    const daysRemaining = getTrialDaysRemaining();
    
    if (daysRemaining <= 0) return "error";
    if (daysRemaining <= 3) return "warning";
    return "info";
  };

  const getBannerMessage = () => {
    const daysRemaining = getTrialDaysRemaining();
    
    if (daysRemaining <= 0) {
      return "Your trial has expired";
    } else if (daysRemaining === 1) {
      return "Your trial expires tomorrow";
    } else {
      return `Your trial expires in ${daysRemaining} days`;
    }
  };

  const getBannerDescription = () => {
    if(!hasActiveSubscription) {
      const daysRemaining = getTrialDaysRemaining();
      
      if(isInOrg) {
        if(isOrganizationOwner) {
          if (daysRemaining <= 0) {
            return "Subscribe to continue using CodePilot.";
          } else {
            return `Subscribe to continue using CodePilot after your trial expires.`;
          }
        } else {
          if (daysRemaining <= 0) {
            return "Your org admin must subscribe to continue using CodePilot.";
          } else {
            return "Your org admin must subscribe to continue using CodePilot after your trial expires.";
          }
        }
      } else {
        if (daysRemaining <= 0) {
          return "Create or join an organization to continue using CodePilot.";
        } else {
          return `Create or join an organization to continue using CodePilot after your trial expires.`;
        }
      }
    }
    return "";
  };

  const getBannerIcon = () => {
    const daysRemaining = getTrialDaysRemaining();
    
    if (daysRemaining <= 0) return <ExclamationCircleOutlined />;
    if (daysRemaining <= 3) return <ClockCircleOutlined />;
    return <CrownOutlined />;
  };

  const getActionButtons = () => {
    console.log("[TrialBanner] isInOrg:", isInOrg);
    console.log("[TrialBanner] isOrganizationOwner:", isOrganizationOwner);
    if (isInOrg && isOrganizationOwner) {
      return (
        <Space>
          <Button
            type="primary"
            icon={<CreditCardOutlined />}
            onClick={() => navigate("/organization/subscription")}
          >
            Manage Subscription
          </Button>
        </Space>
      );
    } else if (!isInOrg) {
      return (
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowCreateModal(true)}
          >
            Create Organization
          </Button>
        </Space>
      );
    }
  };

  const handleCreateSuccess = async () => {
    setShowCreateModal(false);
    await Promise.all([
      refreshOrganizations(),
      fetchAccessStatus(),
    ]);
    onTrialUpdate?.();
  };

  if (!shouldShowBanner()) {
    return null;
  }

  const daysRemaining = getTrialDaysRemaining();
  const actionButtons = getActionButtons();

  return (
    <>
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '0 24px',
        marginBottom: 16,
      }}>
        <Alert
          message={getBannerMessage()}
          description={getBannerDescription()}
          type={getBannerType()}
          showIcon
          icon={getBannerIcon()}
          action={actionButtons}
          closable={false}
          style={{
            borderRadius: 8,
            maxWidth: 800,
            width: '100%',
          }}
        />
      </div>

      <CreateOrganizationModal
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />
    </>
  );
}; 